import React, { useEffect } from "react";
import { Button } from "@mantine/core";
import useMatchPayslipDocumentToEmployeeApiService from "../services/api/MatchPayslipDocumentToEmployeeApiService";
import MatchPayslipDocumentToEmployeeRequest from "../services/request/MatchPayslipDocumentToEmployeeRequest";
import { HttpClientError } from "../../../providers/HttpClient";
import UnmatchedDocumentsResult from "../services/model/UnmatchedDocumentsResult";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type UploadPayslipButtonProps = {
  job: UnmatchedDocumentsResult;
  documentImporterConfiguration: DocumentImporterConfiguration;
  data: MatchPayslipDocumentToEmployeeRequest;
  onSuccess: () => void;
  isDisabled: boolean;
  updateStatus: React.Dispatch<React.SetStateAction<string>>;
};

const UploadPayslipButton: React.FC<UploadPayslipButtonProps> = ({
  job,
  documentImporterConfiguration,
  data,
  updateStatus,
  isDisabled,
  onSuccess,
}) => {
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);
  const { showToast } = useToasts();
  const { isSuccess, isError, isLoading, error, mutate } =
    useMatchPayslipDocumentToEmployeeApiService(data.document_id);

  useEffect(() => {
    if (isError) {
      showToast({
        strapiId: "manual_document_matching_error",
        data: {
          message: HttpClientError(error),
          document_name: documentImporterConfiguration.document_name,
        },
      });
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && !isLoading) {
      showToast({
        strapiId: "manual_document_matching_success",
        data: { document_name: documentImporterConfiguration.document_name },
      });

      updateStatus("Matched");

      setTimeout(() => onSuccess(), 2000);
    }
  }, [isSuccess, isLoading]);

  return isSuccess ? (
    <Button
      type="button"
      onClick={() => mutate(data)}
      variant="filled"
      color="green"
      className="btn-text"
      loading={isLoading}
    >
      {documentImporterConfiguration.document_name} Matched
    </Button>
  ) : (
    <Button
      id="payslips-update-btn"
      type="button"
      onClick={() => {
        trackDocumentsEvent({
          event: `Matching_Modal_Update_${documentImporterConfiguration.document_name}_CTA`,
          description: `User clicks the update ${documentImporterConfiguration.document_name_lower_case} on the matching modal`,
          event_type: "CTA:Click",
          job,
          type: documentImporterConfiguration.document_name_plural,
        });
        mutate(data);
      }}
      variant="filled"
      className="btn-text"
      disabled={isDisabled}
      loading={isLoading}
      color="jem-theme"
    >
      Update {documentImporterConfiguration.document_name}
    </Button>
  );
};

export default UploadPayslipButton;
